/// <reference types="vite/client" />
/**
 * React context for the currently-selected engagement.
 * Phase 9 Y5 (#165).
 *
 * Persistence order on mount:
 *   1. `?engagement=` URL search param (wins — link-shareable)
 *   2. localStorage `levier.engagement`
 *   3. "levier-ia-internal" default
 *
 * On change, the value is mirrored back to both the URL (via
 * history.replaceState) and localStorage.
 *
 * The provider also fetches `/api/engagements/list` once on mount so
 * the picker has names. Engagement list errors are non-fatal — the
 * picker degrades to a free-text input. (See EngagementPicker.tsx.)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface EngagementSummary {
  id: string;
  name: string;
  kind: string;
  status: string;
}

interface EngagementContextValue {
  engagementId: string;
  setEngagementId: (id: string) => void;
  engagements: EngagementSummary[];
  loading: boolean;
}

// Empty string = "Tous les engagements" (no filter). Pages should
// treat `engagementId === ""` as "show everything" and skip passing
// the `engagement` query param when fetching.
const DEFAULT_ENGAGEMENT = "";
const STORAGE_KEY = "levier.engagement";

const EngagementContext = createContext<EngagementContextValue | null>(null);

function readInitial(): string {
  if (typeof window === "undefined") return DEFAULT_ENGAGEMENT;
  const fromUrl = new URLSearchParams(window.location.search).get("engagement");
  if (fromUrl && fromUrl.length > 0) return fromUrl;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) return stored;
  } catch {
    // localStorage unavailable (private mode); ignore.
  }
  return DEFAULT_ENGAGEMENT;
}

function persist(id: string) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (id === DEFAULT_ENGAGEMENT) {
    url.searchParams.delete("engagement");
  } else {
    url.searchParams.set("engagement", id);
  }
  window.history.replaceState({}, "", url.toString());
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

export function EngagementProvider({ children }: { children: ReactNode }) {
  const [engagementId, setEngagementIdState] = useState<string>(() => readInitial());
  const [engagements, setEngagements] = useState<EngagementSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    fetch("/api/engagements/list", { signal: ctrl.signal })
      .then((res) => (res.ok ? (res.json() as Promise<EngagementSummary[]>) : []))
      .then((rows) => {
        setEngagements(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("[useEngagement] /api/engagements/list fetch failed.", err);
        }
        setLoading(false);
      });
    return () => ctrl.abort();
  }, []);

  const setEngagementId = useCallback((id: string) => {
    setEngagementIdState(id);
    persist(id);
  }, []);

  const value = useMemo<EngagementContextValue>(
    () => ({ engagementId, setEngagementId, engagements, loading }),
    [engagementId, setEngagementId, engagements, loading],
  );

  return <EngagementContext.Provider value={value}>{children}</EngagementContext.Provider>;
}

export function useEngagement(): EngagementContextValue {
  const ctx = useContext(EngagementContext);
  if (!ctx) {
    throw new Error("useEngagement must be used inside <EngagementProvider>");
  }
  return ctx;
}
